import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { Alert, Box, Button, Stack, styled, Typography } from '@mui/material';
import { type ReactNode } from 'react';
import type { NodeChange, NodeMouseHandler } from 'reactflow';
import { Loader } from 'src/components';
import { WorkflowDiagram } from 'src/pages/Settings/workflows/components';
import type {
  WorkflowGraphEdge,
  WorkflowGraphNode,
  WorkflowLayoutDirection,
} from 'src/pages/Settings/workflows/utils/workflow-helpers';

export const WorkflowDetailCard = styled(Stack)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  margin: theme.spacing(0, 2, 2),
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

export const DefinitionCodeBox = styled('pre')(({ theme }) => ({
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  margin: 0,
  fontFamily: 'IBM Plex Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.85rem',
  maxHeight: '100%',
  minHeight: 280,
  height: '100%',
  overflow: 'auto',
  flex: 1,
}));

const CenteredStateStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  textAlign: 'center',
  padding: theme.spacing(4),
}));

export const WorkflowEmptyState = () => (
  <CenteredStateStack spacing={1}>
    <Typography variant="h6">No workflows yet</Typography>
    <Typography variant="body2" color="text.secondary">
      Create one with New › Workflow.
    </Typography>
  </CenteredStateStack>
);

interface VersionEmptyStateProps {
  workflowName?: string;
}

export const VersionEmptyState = ({ workflowName }: VersionEmptyStateProps) => (
  <CenteredStateStack spacing={1}>
    <Typography variant="subtitle1">
      No versions found for {workflowName || 'this workflow'}.
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Select a different workflow or create a draft version.
    </Typography>
  </CenteredStateStack>
);

export const WorkflowSelectionEmptyState = () => (
  <CenteredStateStack spacing={1}>
    <Typography variant="h6">No workflow selected</Typography>
    <Typography variant="body2" color="text.secondary">
      Select a workflow and version to view details
    </Typography>
  </CenteredStateStack>
);

/**
 * The green dot that marks the active version. Lives here rather than in the toolbar so the
 * header and the version select cannot drift apart on what "active" looks like.
 */
export const ActiveStatusDot = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
}));

interface WorkflowDetailsHeaderProps {
  workflowName: string;
  versionNumber: number;
  statusLabel: 'Active' | 'Draft';
  updatedAtLabel: string;
  /** "4 steps", or "2 of 4 steps shown" while some are folded away. */
  stepsLabel?: string;
  showRawDefinition: boolean;
  onToggleDefinition: () => void;
  disableToggle: boolean;
  viewOnly: boolean;
  onCopyToNewWorkflow: () => void;
  copyToNewWorkflowDisabled: boolean;
  /**
   * The active version of the partner's own workflow: Edit opens the newest draft, or makes one
   * from this version when there's none newer.
   * Absent on a draft (already editable) and on a platform workflow (view only).
   */
  onEdit?: () => void;
  isStartingDraft?: boolean;
  /** A draft being edited: its save state, the problem count, Discard and Save draft. */
  draft?: WorkflowDraftHeaderState;
}

export interface WorkflowDraftHeaderState {
  isDirty: boolean;
  isSaving: boolean;
  stepsNeedingAttention: number;
  /** Opens the first step with a problem. */
  onShowFirstProblem: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

export const WorkflowDetailsHeader = ({
  workflowName,
  versionNumber,
  statusLabel,
  updatedAtLabel,
  stepsLabel,
  showRawDefinition,
  onToggleDefinition,
  disableToggle,
  viewOnly,
  onCopyToNewWorkflow,
  copyToNewWorkflowDisabled,
  onEdit,
  isStartingDraft = false,
  draft,
}: WorkflowDetailsHeaderProps) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    spacing={2}
    alignItems={{ xs: 'flex-start', md: 'center' }}
    justifyContent="space-between"
    sx={{
      px: 3,
      py: 2.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}
  >
    <Stack spacing={0.75} sx={{ minWidth: { xs: 0, md: 260 }, flex: 1 }}>
      <Typography variant="h6">{workflowName}</Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        color="text.secondary"
      >
        <Typography variant="body2">Version {versionNumber}</Typography>
        <Typography variant="body2">•</Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {statusLabel === 'Active' ? <ActiveStatusDot /> : null}
          <Typography
            variant="body2"
            fontWeight={statusLabel === 'Active' ? 600 : 500}
            color={statusLabel === 'Active' ? 'success.main' : 'text.secondary'}
          >
            {statusLabel}
          </Typography>
        </Stack>
        <Typography variant="body2">•</Typography>
        <Typography variant="body2">Updated {updatedAtLabel}</Typography>
        {stepsLabel ? (
          <>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">{stepsLabel}</Typography>
          </>
        ) : null}
        {viewOnly ? (
          <>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2" fontWeight={600}>
              Platform workflow · view only
            </Typography>
          </>
        ) : null}
        {onEdit ? (
          <>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2">Read only. Edit opens a draft to change.</Typography>
          </>
        ) : null}
        {draft?.isDirty ? (
          <>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2" color="text.primary">
              Unsaved changes
            </Typography>
          </>
        ) : null}
        {/* In the status line rather than the button row, so the buttons never move */}
        {draft && draft.stepsNeedingAttention > 0 ? (
          <>
            <Typography variant="body2">•</Typography>
            <Button
              size="small"
              color="error"
              startIcon={<ErrorOutlineIcon fontSize="small" />}
              onClick={draft.onShowFirstProblem}
              sx={{ py: 0, minHeight: 0 }}
            >
              {draft.stepsNeedingAttention === 1
                ? '1 step needs attention'
                : `${draft.stepsNeedingAttention} steps need attention`}
            </Button>
          </>
        ) : null}
      </Stack>
    </Stack>

    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
      justifyContent="flex-end"
    >
      <Button
        variant="outlined"
        startIcon={<ContentCopyIcon />}
        onClick={onCopyToNewWorkflow}
        disabled={copyToNewWorkflowDisabled}
      >
        Copy to new workflow
      </Button>
      <Button
        variant="outlined"
        startIcon={showRawDefinition ? <HubOutlinedIcon /> : <CodeIcon />}
        onClick={onToggleDefinition}
        disabled={disableToggle}
      >
        {showRawDefinition ? 'View Diagram' : 'View JSON'}
      </Button>
      {onEdit ? (
        <Button
          variant="contained"
          startIcon={<EditOutlinedIcon />}
          onClick={onEdit}
          disabled={isStartingDraft}
        >
          Edit
        </Button>
      ) : null}
      {draft ? (
        <>
          <Button onClick={draft.onDiscard} disabled={!draft.isDirty || draft.isSaving}>
            Discard
          </Button>
          {/* Enabled with steps needing attention too: pressing it says what's wrong and opens the
              step, where a greyed-out button would only hint at it on hover */}
          <Button
            variant="contained"
            onClick={draft.onSave}
            disabled={!draft.isDirty || draft.isSaving}
          >
            Save draft
          </Button>
        </>
      ) : null}
    </Stack>
  </Stack>
);

interface DefinitionContentProps {
  pendingDiagram: boolean;
  hasDiagramNodes: boolean;
  showRawDefinition: boolean;
  diagramNodes: WorkflowGraphNode[];
  diagramEdges: WorkflowGraphEdge[];
  layoutDirection: WorkflowLayoutDirection;
  definitionError?: string;
  formattedDefinitionText: string;
  onDiagramNodeClick: NodeMouseHandler;
  onNodesChange: (changes: NodeChange[]) => void;
  onLayoutChange: (direction: WorkflowLayoutDirection) => void;
  /** A click on empty canvas: closes the open step. */
  onPaneClick: () => void;
}

export const DefinitionContent = ({
  pendingDiagram,
  hasDiagramNodes,
  showRawDefinition,
  diagramNodes,
  diagramEdges,
  layoutDirection,
  definitionError,
  formattedDefinitionText,
  onDiagramNodeClick,
  onNodesChange,
  onLayoutChange,
  onPaneClick,
}: DefinitionContentProps): ReactNode => {
  if (pendingDiagram) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%" minHeight={320}>
        <Loader />
      </Stack>
    );
  }

  if (hasDiagramNodes && !showRawDefinition) {
    return (
      <Stack sx={{ flex: 1, minHeight: 0, width: '100%' }}>
        <WorkflowDiagram
          nodes={diagramNodes}
          edges={diagramEdges}
          height="100%"
          onNodeClick={onDiagramNodeClick}
          onNodesChange={onNodesChange}
          layoutDirection={layoutDirection}
          onLayoutChange={onLayoutChange}
          onPaneClick={onPaneClick}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={1} height="100%" sx={{ flex: 1, minHeight: 0, width: '100%' }}>
      {definitionError ? (
        <Alert severity="warning" sx={{ alignSelf: 'flex-start' }}>
          {definitionError}
        </Alert>
      ) : null}
      <DefinitionCodeBox>{formattedDefinitionText}</DefinitionCodeBox>
    </Stack>
  );
};

export const WorkflowDetailContentContainer = ({ children }: { children: ReactNode }) => (
  <Stack
    sx={{
      flex: 1,
      minHeight: 0,
      p: 3,
      backgroundColor: 'background.default',
      '& > *': {
        flex: 1,
        minHeight: 0,
        width: '100%',
      },
    }}
  >
    {children}
  </Stack>
);
